<template>
  <div>
    <DriverInfo :driverSource="model.order" center />
    <el-form
      label-width="160px"
      ref="formModelRef"
      label-position="left"
      :model="formModel"
      :rules="formRules"
      >
      <Card title="签约信息">
        <div class="withhold-container">
          <el-form-item label="租赁合同模板" prop="templateId" v-if="model.isRent === IS_RENT.SIGN">
            <span v-if="model.rentContract.templateFixed === 1">
              {{ model.rentContract.templateName }}
            </span>
            <el-select
              style="width: 360px"
              v-else
              v-model="formModel.templateId"
              filterable
              clearable
              >
              <el-option
                v-for="item in contractTemplateList"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="签约主体" prop="licenceNo">
            <span v-if="model.licenceNoFixed === 1">
              {{ model.merchantName }}
            </span>
            <el-select
              v-else
              filterable
              clearable
              v-model="formModel.licenceNo"
              @change="changeLicenceNo"
              >
              <el-option
                v-for="item in merchantList"
                :key="item.licenceNo"
                :label="item.merchantName"
                :value="item.licenceNo"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="签约网约平台" prop="spId">
            <span v-if="model.withholdConfig.spFixed === 1">
              {{ model.withholdConfig.spName }}
            </span>
            <el-select
              style="width: 360px"
              v-else
              v-model="formModel.spId"
              filterable
              clearable
              @change="changeSpId"
              :disabled="!formModel.licenceNo"
              >
              <el-option
                v-for="item in spList"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="支付渠道" prop="channelType">
            <el-select
              style="width: 360px"
              v-model="formModel.channelType"
              :disabled="!formModel.spId"
              filterable
              clearable
              >
              <el-option
                v-for="item in channelTypeList"
                :key="item.channelId"
                :label="item.channelName"
                :value="item.channelId"
              />
            </el-select>
          </el-form-item>
        </div>
      </Card>
      <Card title="支付配置">
        <div class="withhold-container">
          <DeductionOrderTable
            ref="deductionOrderTableRef"
            :config="deductionOrderConfig"
            :rentCategory="rentCategory"
            :isDetail="isWithholdConfigFixed"
          />
        </div>
      </Card>
    </el-form>
    <div class="footer">
      <el-button @click="emits('cancel')">取消</el-button>
      <el-button @click="handlePreviewContract" :loading="previewLoading">预览合同</el-button>
      <el-button
        v-if="showSubmit"
        type="primary"
        @click="signContract"
        :loading="signLoading"
        >
        {{ submitText }}
      </el-button>
      <el-button
        v-if="showRevert"
        type="primary"
        @click="revertSign"
        >
        撤销签署
      </el-button>
    </div>
  </div>
</template>
<script setup>
import { getSpNameList, orderMerchantList } from '@/api/company'
import { contractTmpList } from '@/api/domain/organize'
import { getSubmitDingTalkShow } from '@/api/global'
import { cancelSignContract, previewContract, startSignWithhold } from '@/api/withhold.js'
import { CONTRACT_STATE_MAP, SIGN_APPROVAL_STATE_MAP } from '@/pages/orders/components/contractSigningForm/config/enum'
import { ORDER_STATE } from '@/store/dict/order'
import { CATEGORY_CODE_MAP } from '@/store/dict/rentPlanDict.js'
import { useMessageBox } from '@/utils/useMessageBox'
import { Message, MessageBox } from '@qs/ui'
import { computed, nextTick, onMounted, ref, toRefs, watch } from 'vue'
import { FREQUENCY_MAP, IS_RENT, PERIOD_LIMIT_MAP } from '../config/enum.js'
import DeductionOrderTable from './DeductionOrderTable.vue'
import DriverInfo from './DriverInfo.vue'

const emits = defineEmits(['submit', 'cancel'])

const { showESignQuotaDialog } = useMessageBox()

const props = defineProps({
  // 订单
  model: {
    type: Object,
    default: () => ({}),
  },
  rentCategory: {
    type: [String, Number],
    default: '',
  },
  // 是否选择了网约钱包划扣
  isWalletWithhold: {
    type: Number,
    default: null,
  },
  // 是否选择了支付宝划扣
  isAlipayWithhold: {
    type: Number,
    default: null,
  },
})

const { model, rentCategory } = toRefs(props)

const formModelRef = ref(null)
const deductionOrderTableRef = ref(null)

// 预览合同加载标识
const previewLoading = ref(false)
// 签署加载标识
const signLoading = ref(false)
// 提交按钮文案
const submitText = ref('发起签署')

// 签约信息
const formModel = ref({
  orderId: '',
  templateId: '', // 租赁合同模板
  licenceNo: '', // 签约主体
  spId: '', // 签约网约平台
  channelType: '', // 支付渠道
})

// 支付配置
const deductionOrderConfig = ref({
  type: '', // 支付频率
  startDate: '', // 周扣日期
  periodLimitType: '', // 支付限额类型
  limitCount: '', // 支付限额数值
  deductionList: [], // 支付费项列表
})

const formRules = {
  templateId: [
    { required: true, message: '请选择合同模板' },
  ],
  licenceNo: [
    { required: true, message: '请选择签约主体' },
  ],
  spId: [
    { required: true, message: '请选择签约网约平台' },
  ],
  channelType: [
    { required: true, message: '请选择支付渠道' },
  ],
}

const contractTemplateList = ref([])
const merchantList = ref([])
const spList = ref([])
const channelTypeList = computed(() => {
  const selectItem = spList.value.find((item) => item.value === formModel.value.spId)
  return selectItem?.channelTypeList
})

// 是否展示发起/审批按钮
const showSubmit = computed(() => {
  const { contractState, startSignApprovalState } = model.value.withholdContract
  const list = [
    CONTRACT_STATE_MAP.TO_SIGN,
    CONTRACT_STATE_MAP.REVERT,
    CONTRACT_STATE_MAP.OUT_DATE,
    CONTRACT_STATE_MAP.REJECT,
    CONTRACT_STATE_MAP.FAIL,
  ]
  return list.includes(contractState) &&
    startSignApprovalState !== SIGN_APPROVAL_STATE_MAP.PENDING_APPROVAL
})

// 是否展示撤销按钮
const showRevert = computed(() => {
  const { contractState, startSignApprovalState } = model.value.withholdContract
  return contractState === CONTRACT_STATE_MAP.SIGNING ||
    startSignApprovalState === SIGN_APPROVAL_STATE_MAP.PENDING_APPROVAL
})

// 支付配置是否被固定（禁用）
const isWithholdConfigFixed = computed(() => {
  return model.value.withholdConfig?.withholdConfigFixed === 1
})

// 签署合同
const signContract = async () => {
  formModelRef.value.validate(async (valid) => {
    if (!valid) return

    const deductionData = await deductionOrderTableRef.value.getData()
    if (
      deductionData.feeTypeList.length === 0
      && rentCategory.value !== CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI
    ) {
      Message.error('请选择扣款费项')
      return
    }
    signLoading.value = true
    const {
      licenceNo,
      templateId,
      spId,
      channelType,
    } = formModel.value

    startSignWithhold({
      orderId: model.value.orderId,
      licenceNo,
      rentContract: { templateId },
      withholdConfig: {
        feeTypeAndAmountList: deductionData.feeTypeAndAmountList || [],
        type: deductionData.type,
        startDate: deductionData.startDate,
        periodLimit: deductionData.limitCount,
        spId,
        channelType,
      },
      isAlipayWithhold: props.isAlipayWithhold,
    }).then(({ code }) => {
      if (code === 1) {
        emits('submit')
        Message.success('提交成功')
      }
    }).catch((error) => {
      if (error.code === 13997 && error.msg.includes('签约份额不足')) {
        showESignQuotaDialog(model.value.licenceNo)
      } else {
        Message.error(error.msg)
      }
    }).finally(() => {
      signLoading.value = false
    })
  })
}

// 预览合同
const handlePreviewContract = async () => {
  formModelRef.value.validate(async (valid) => {
    if (!valid) return
    const deductionData = await deductionOrderTableRef.value.getData()
    if (
      deductionData.feeTypeList.length === 0
      && rentCategory.value !== CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI
    ) {
      Message.error('请选择扣款费项')
      return
    }
    previewLoading.value = true
    const { licenceNo, templateId, spId, channelType } = formModel.value
    previewContract({
      orderId: model.value.orderId,
      licenceNo,
      rentContract: {
        templateId,
      },
      withholdConfig: {
        feeTypeAndAmountList: deductionData.feeTypeAndAmountList || [],
        type: deductionData.type,
        startDate: deductionData.startDate,
        periodLimit: deductionData.limitCount,
        spId,
        channelType,
      },
      isAlipayWithhold: props.isAlipayWithhold,
    }).then(({ code, data }) => {
      if (code === 1) {
        window.open(data, '_blank')
      }
    }).finally(() => {
      previewLoading.value = false
    })
  })
}

// 撤销签署
const revertSign = () => {
  MessageBox({
    title: '提示',
    message: '您确定要撤回合同签署吗',
    showCancelButton: true,
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
    beforeClose: async (action, instance, done) => {
      if (action === 'confirm') {
        instance.confirmButtonLoading = true
        cancelSignContract({
          orderId: model.value.orderId,
        }).then(({ code }) => {
          if (code === 1) {
            Message.success('撤销成功')
            emits('submit')
            emits('cancel')
            done()
          }
        }).finally(() => { instance.confirmButtonLoading = false })
      } else {
        done()
      }
    },
  }).catch(() => null)
}

// 查询模版列表
const getTemplateList = async () => {
  // 查询已启用的模版
  const { code, data } = await contractTmpList({ state: 1 })
  if (code === 1) {
    contractTemplateList.value = data.items.map((item) => ({
      label: item.name,
      value: item.id,
    }))
  }
}

// 查询签约主体
const getMerchantList = async () => {
  const { code, data } = await orderMerchantList({
    isWalletWithhold: props.isWalletWithhold,
    isAlipayWithhold: props.isAlipayWithhold,
  })
  if (code === 1) {
    merchantList.value = data
  }
}

// 查询签约网约平台
const getSpList = async (val) => {
  if (!val) return
  const { code, data } = await getSpNameList({ licenceNo: val })
  if (code === 1) {
    spList.value = data.map((item) => ({
      label: item.spName,
      value: item.spId,
      channelTypeList: item.channelTypeList,
    }))
  }
}

// 签约主体变更
watch(() => formModel.value.licenceNo, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    getSpList(formModel.value.licenceNo)
  }
})
const changeLicenceNo = () => {
  // 如果网约平台被锁定，则不更新网约平台
  if (model.value.withholdConfig.spFixed !== 1) {
    formModel.value.spId = ''
  }
  formModel.value.channelType = ''
  nextTick(() => {
    formModelRef.value?.clearValidate(['spId', 'channelType'])
  })
}

// 网约平台变更
const changeSpId = (val) => {
  formModel.value.channelType = ''
  if (val) {
    const selectedSpInfo = spList.value.find((item) => item.value === val)
    updateChannelType(selectedSpInfo.channelTypeList)
  }
  nextTick(() => {
    formModelRef.value?.clearValidate('channelType')
  })
}
const updateChannelType = (val) => {
  if (Array.isArray(val) && val.length === 1) {
    formModel.value.channelType = val[0].channelId
  }
}
watch(() => channelTypeList, updateChannelType)


watch(() => props.model, (val) => {
  const { orderId, licenceNo, rentContract, withholdConfig, order = {}, isRent } = val
  const { templateId } = rentContract
  const { spId, channelType, type, periodLimit: limitCount, startDate } = withholdConfig

  // 更新表单数据
  formModel.value = {
    orderId,
    templateId, // 租赁合同模板
    licenceNo, // 签约主体
    spId, // 签约网约平台
    channelType, // 支付渠道
  }

  // 更新支付配置数据
  deductionOrderConfig.value = {
    type: type || FREQUENCY_MAP.DAY, // 支付频率
    startDate: startDate || '',
    periodLimitType: limitCount ? PERIOD_LIMIT_MAP.LIMIT : PERIOD_LIMIT_MAP.NO_LIMIT,
    limitCount: limitCount || '',
    deductionList: withholdConfig?.allFeeTypeList || [], // 支付费项列表
  }

  nextTick(() => {
    formModelRef.value?.clearValidate()
  })
  // 是否开启审批流
  getSubmitDingTalkShow({
    bizType: 26,
  }).then(({ code, data }) => {
    if (code === 1 && data && order.state === ORDER_STATE.TO_CONFIRM && isRent === IS_RENT.SIGN) {
      submitText.value = '提交签署审批'
    }
  })
}, {
  immediate: true,
})

onMounted(() => {
  getTemplateList()
  getMerchantList()
})

// 暴露给外部组件使用
defineExpose({
  formModel,
})

</script>
<style lang="scss" scoped>
.withhold-container {
  margin: 0 100px;
}
.footer {
  width: 100%;
  text-align: right;
  padding: 10px;
  position: fixed;
  right: 10px;
  bottom: 0;
  background: #F3F3F4;
  z-index: 999;
}

</style>
